import { useState, useRef, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Heart, X, Sparkles, Play, FolderOpen, ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "../context/LanguageContext";
import { usePageContent } from "../hooks/usePageContent";
import { API_URL as API } from "../config";

const GOFUNDME_URL = "https://gofund.me/274183352";
const VIDEO_DEFAULT = "https://customer-assets.emergentagent.com/job_c5baa259-20bb-43f4-9718-d5116589bbe8/artifacts/o90d4mff_dia%20de%20entrenamiento%20con%20jorge%20el%20curioso.mp4";
const FALLBACK_IMAGES = [
  { src: "https://customer-assets.emergentagent.com/job_4e563cac-59c1-451d-b6a2-231b424045b8/artifacts/02cy67vf_image.png", title: "Training Day", span: "md:col-span-2 md:row-span-2" },
  { src: "https://customer-assets.emergentagent.com/job_4e563cac-59c1-451d-b6a2-231b424045b8/artifacts/f57s2cat_image.png", title: "Team Unity", span: "" },
  { src: "https://customer-assets.emergentagent.com/job_4e563cac-59c1-451d-b6a2-231b424045b8/artifacts/gkpyigf5_image.png", title: "Practice", span: "" },
  { src: "https://images.unsplash.com/photo-1643101804927-269c43404638?w=600", title: "Beautiful Game", span: "md:col-span-2" },
];

const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold, rootMargin: '50px' });
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [threshold]);
  return { ref, isVisible };
};

const AnimatedSection = ({ children, className = "", animation = "fly-up", delay = 0 }) => {
  const { ref, isVisible } = useScrollReveal(0.1);
  const baseTransform = { 'fly-left': 'translateX(-80px)', 'fly-right': 'translateX(80px)', 'fly-up': 'translateY(60px)', 'fly-down': 'translateY(-60px)', 'zoom': 'scale(0.8)' };
  const style = { opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateX(0) translateY(0) scale(1)' : baseTransform[animation] || 'translateY(60px)', transition: `opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms, transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms` };
  return <div ref={ref} className={className} style={style}>{children}</div>;
};

function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([^&?/]+)/);
  return match ? match[1] : null;
}

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [allMedia, setAllMedia] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [albumMedia, setAlbumMedia] = useState([]);
  const { lang, t } = useLanguage();
  const { get } = usePageContent("gallery");

  const featuredVideo = get("featured_video", VIDEO_DEFAULT);

  useEffect(() => {
    fetch(`${API}/api/gallery/albums`).then(r => r.json()).then(setAlbums).catch(() => {});
    fetch(`${API}/api/gallery/media/all`).then(r => r.json()).then(setAllMedia).catch(() => {});
  }, []);

  const loadAlbum = (album) => {
    setSelectedAlbum(album);
    fetch(`${API}/api/gallery/albums/${album.id}`).then(r => r.json()).then(d => setAlbumMedia(d.media || [])).catch(() => {});
  };

  const title = lang === "es" ? "Galeria | Curious 123" : "Gallery | Curious 123 Youth Soccer";
  const desc = lang === "es" ? "Fotos y videos del programa de futbol juvenil Curious 123." : "View photos and videos from Curious 123 youth soccer program.";

  // Combine CMS media with fallback images
  const galleryImages = allMedia.length > 0
    ? allMedia.filter(m => m.type === "image").map((m, i) => ({ src: m.url, title: m.title || "Photo", alt: m.description || m.title || "Photo", span: i === 0 ? "md:col-span-2 md:row-span-2" : i === 3 ? "md:col-span-2" : "" }))
    : FALLBACK_IMAGES.map(f => ({ ...f, alt: f.title }));

  const galleryVideos = allMedia.filter(m => m.type === "video");

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Curious 123" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <section className="pt-32 pb-20 bg-gradient-to-b from-[#F0F9FF] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-up" className="text-center max-w-4xl mx-auto">
            <span className="inline-block bg-[#87CEEB] text-black px-4 py-2 rounded-full text-sm font-semibold mb-6 uppercase tracking-wider">{t.gallery.badge}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black leading-tight mb-6 font-['Kanit']">{get("hero_title", t.gallery.title)}</h1>
            <p className="text-lg text-gray-700">{get("hero_subtitle", t.gallery.subtitle)}</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Featured Video */}
      <section className="py-20 bg-black" data-testid="video-gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-down" className="text-center mb-12">
            <span className="inline-block bg-[#87CEEB] text-black px-4 py-2 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider">{t.gallery.videoBadge}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-['Kanit']">{t.gallery.videoTitle}</h2>
          </AnimatedSection>
          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-900 aspect-video" data-testid="gallery-video-container">
              {videoPlaying ? (
                getYouTubeId(featuredVideo) ? (
                  <iframe src={`https://www.youtube.com/embed/${getYouTubeId(featuredVideo)}?autoplay=1`} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen title="Video" />
                ) : (
                  <video className="w-full h-full object-cover" src={featuredVideo} autoPlay controls playsInline />
                )
              ) : (
                <div className="relative w-full h-full cursor-pointer group" onClick={() => setVideoPlaying(true)}>
                  {getYouTubeId(featuredVideo) ? (
                    <img src={`https://img.youtube.com/vi/${getYouTubeId(featuredVideo)}/maxresdefault.jpg`} alt="Video" className="w-full h-full object-cover" />
                  ) : (
                    <img src={galleryImages[0]?.src} alt="Video" className="w-full h-full object-cover" />
                  )}
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all flex items-center justify-center">
                    <div className="w-20 h-20 bg-[#87CEEB] rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform animate-pulse-glow">
                      <Play className="w-10 h-10 text-black ml-1" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Albums */}
      {albums.length > 0 && !selectedAlbum && (
        <section className="py-20 bg-[#F0F9FF]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection animation="fly-down" className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 font-['Kanit']">{lang === "es" ? "Albums" : "Albums"}</h2>
            </AnimatedSection>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {albums.map(a => (
                <div key={a.id} onClick={() => loadAlbum(a)} className="bg-white rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-all group" data-testid={`public-album-${a.id}`}>
                  <div className="h-48 bg-gray-100 overflow-hidden">
                    {a.cover_image ? (
                      <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><FolderOpen className="w-12 h-12 text-gray-300" /></div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-lg text-gray-900 font-['Kanit']">{a.title}</h3>
                    {a.description && <p className="text-sm text-gray-500 mt-1">{a.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Album Detail View */}
      {selectedAlbum && (
        <section className="py-20 bg-[#F0F9FF]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button onClick={() => { setSelectedAlbum(null); setAlbumMedia([]); }}
              className="flex items-center gap-2 text-gray-600 hover:text-black mb-6 text-sm font-medium transition-colors">
              <ArrowLeft className="w-4 h-4" /> {lang === "es" ? "Todos los Albums" : "All Albums"}
            </button>
            <h2 className="text-3xl font-bold text-black mb-2 font-['Kanit']">{selectedAlbum.title}</h2>
            {selectedAlbum.description && <p className="text-gray-500 mb-8">{selectedAlbum.description}</p>}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {albumMedia.map((m, i) => (
                <div key={m.id} className="rounded-xl overflow-hidden cursor-pointer group relative" onClick={() => m.type === "image" && setSelectedImage({ src: m.url, title: m.title, alt: m.description || m.title })}>
                  {m.type === "image" ? (
                    <img src={m.url} alt={m.title} className="w-full h-48 object-cover group-hover:scale-105 transition-transform" />
                  ) : (
                    <div className="w-full h-48 bg-gray-900 flex items-center justify-center relative">
                      {m.youtube_url ? (
                        <img src={`https://img.youtube.com/vi/${getYouTubeId(m.youtube_url)}/mqdefault.jpg`} alt={m.title} className="w-full h-full object-cover" />
                      ) : m.url ? (
                        <video src={m.url} className="w-full h-full object-cover" />
                      ) : null}
                      <Play className="absolute w-10 h-10 text-white/80" />
                    </div>
                  )}
                  {m.title && <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3"><p className="text-white text-sm font-medium">{m.title}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Videos from CMS */}
      {galleryVideos.length > 0 && !selectedAlbum && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-black mb-8 font-['Kanit']">{lang === "es" ? "Videos" : "Videos"}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryVideos.map(v => (
                <div key={v.id} className="rounded-2xl overflow-hidden bg-gray-900 shadow-lg">
                  {v.youtube_url ? (
                    <iframe src={`https://www.youtube.com/embed/${getYouTubeId(v.youtube_url)}`} className="w-full aspect-video" allow="encrypted-media" allowFullScreen title={v.title} />
                  ) : v.url ? (
                    <video src={v.url} controls className="w-full aspect-video" />
                  ) : null}
                  {v.title && <div className="p-4"><p className="text-white font-medium">{v.title}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Photo Grid (fallback or CMS images) */}
      {!selectedAlbum && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <AnimatedSection animation="fly-down" className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-black mb-4 font-['Kanit']">{t.gallery.photosTitle}</h2>
            </AnimatedSection>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[200px]">
              {galleryImages.map((image, index) => (
                <div key={index} className={`animate-fade-in ${image.span}`} style={{ animationDelay: `${index * 100}ms` }}>
                  <div className="gallery-item rounded-2xl overflow-hidden cursor-pointer relative group h-full" onClick={() => setSelectedImage(image)} data-testid={`gallery-image-${index}`}>
                    <img src={image.src} alt={image.alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity text-center text-white p-4">
                        <Sparkles className="w-8 h-8 mx-auto mb-2" /><p className="font-bold font-['Kanit']">{image.title}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 bg-[#87CEEB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection animation="zoom">
            <h2 className="text-3xl font-bold text-black mb-6 font-['Kanit']">{t.gallery.ctaTitle}</h2>
            <p className="text-lg text-black/80 mb-8">{t.gallery.ctaDesc}</p>
            <a href={GOFUNDME_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-black text-white hover:bg-gray-900 rounded-full px-10 py-4 text-lg font-bold uppercase tracking-wider"><Heart className="w-5 h-5 mr-2" />{t.nav.donateNow}</Button>
            </a>
          </AnimatedSection>
        </div>
      </section>

      {selectedImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setSelectedImage(null)}>
          <div className="relative max-w-4xl w-full">
            <img src={selectedImage.src} alt={selectedImage.alt} className="w-full h-auto rounded-2xl" />
            <div className="absolute bottom-4 left-4 text-white">
              <p className="font-bold text-xl font-['Kanit']">{selectedImage.title}</p>
            </div>
            <button className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors" onClick={() => setSelectedImage(null)}>
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default GalleryPage;
